import React, {useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Button, Form, Radio} from 'antd';
import DefaultLayout from 'components/DefaultLayout';
import {Input, Select, Switch} from 'components/Form';
import {useParams, useNavigate} from 'react-router-dom';
import {usePhishing} from "hooks/Phishing/usePhishing";
import {config} from 'config/config';

const formItemLayout = {
    labelCol: {span: 8},
    wrapperCol: {span: 6},
};

const TemplateForm: React.FC<any> = ({}) => {
    const {getTemplate, saveTemplate} = usePhishing();

    const [ownerType, setOwnerType] = useState<string | undefined>(undefined);

    const navigate = useNavigate();
    const params = useParams();
    const [form] = Form.useForm();
    const templateId = params.id;
    const intl = useIntl();

    useEffect(() => {
        if (templateId) {
            loadLectures(templateId);
        }
    }, [])

    const loadLectures = async (id: string) => {
        const response = await getTemplate(id);

        if (!response) {
            return;
        }

        setOwnerType(response.ownerType ?? undefined);

        form.setFieldsValue(response);
    }

    const ownerTypeChangeHandle = (value: string | undefined) => {
        setOwnerType(value);
        form.setFieldValue('ownerUuid', undefined);
    }

    const submitForm = async (values: any) => {
        const response = await saveTemplate(values, templateId ? templateId : null);

        // saveTemplate returns null on a failed request — the error is already shown
        if (!response) {
            return;
        }

        navigate(`/phishing/templates/${response.id}`)
    };

    // The API requires every setting, so unopened switches and an untouched
    // difficulty must still submit a value. Defaults match the column defaults.
    const initialValues = {
        allowSmartDephish: true,
        customPhishingCampaigns: true,
        difficulty: 'LOW',
        industry: [],
    };

    return (
        <>
            <Form form={form} onFinish={submitForm} initialValues={initialValues} {...formItemLayout}>
                <Input name="title"
                       label={intl.formatMessage({id: 'phishing.simulation_template_universal_name'})}
                       validation={{required: true}}/>
                <Switch isFormItem hasDefaultLayout name='allowSmartDephish'
                        label={intl.formatMessage({id: 'phishing.allow_smart_dephish'})}/>
                <Switch isFormItem hasDefaultLayout name='customPhishingCampaigns'
                        label={intl.formatMessage({id: 'phishing.custom_phishing_campaigns'})}/>
                <Form.Item
                    name="difficulty"
                    label={intl.formatMessage({id: "phishing.difficulty"})}
                >
                    <Radio.Group>
                        <Radio value={'LOW'}><FormattedMessage id='phishing.low'/></Radio>
                        <Radio value={'MEDIUM'}><FormattedMessage id='phishing.medium'/></Radio>
                        <Radio value={'HIGH'}><FormattedMessage id='phishing.high'/></Radio>
                    </Radio.Group>
                </Form.Item>
                <Select
                    allowClear
                    name='ownerType'
                    label={intl.formatMessage({id: 'phishing.template_owner_type'})}
                    hint={intl.formatMessage({id: 'phishing.template_owner_hint'})}
                    manualOptions={{
                        ORGANIZATION: intl.formatMessage({id: 'general.reseller'}),
                        CUSTOMER: intl.formatMessage({id: 'general.customer'}),
                    }}
                    onChange={ownerTypeChangeHandle}
                />
                {ownerType === 'CUSTOMER' &&
                    <Select
                        allowClear
                        showSearch
                        name='ownerUuid'
                        dataKey='customers'
                        label={intl.formatMessage({id: 'general.customer'})}
                        url={config.api.routes.backend.selectCustomersUuid}
                        validation={{required: true}}
                        filterOption={(input: string, option: any) => {
                            return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                    />
                }
                {ownerType === 'ORGANIZATION' &&
                    <Select
                        allowClear
                        showSearch
                        name='ownerUuid'
                        dataKey='organizations'
                        label={intl.formatMessage({id: 'general.reseller'})}
                        url={config.api.routes.backend.selectOrganizationsUuid}
                        validation={{required: true}}
                        filterOption={(input: string, option: any) => {
                            return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                    />
                }
                <Select
                    allowSearch
                    mode="multiple"
                    showArrow
                    showSearch
                    name='industry'
                    label={intl.formatMessage({id: "phishing.industry_types"})}
                    filterOption={(input: string, option: any) => {
                        return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    url={'/api/v1/enum/industry-types'}
                />
            </Form>
            <DefaultLayout.PageFooter
                right={
                    <Button onClick={form.submit} className='save-button'>
                        <FormattedMessage id="general.submit"/>
                    </Button>
                }
            />
        </>
    )
}

export default TemplateForm;
